import { MASAR } from "@/constants/testIds";
import { SectionLabel } from "@/components/masar/HowItWorks";
import { TiltCard } from "@/components/masar/Interactive";
import { useLanguage } from "@/i18n/LanguageContext";
import {
  Route,
  Users,
  Store,
  MessageCircle,
  ShieldAlert,
} from "lucide-react";

const serviceMeta = [
  { icon: Route },
  { icon: Users },
  { icon: Store },
  { icon: MessageCircle },
  { icon: ShieldAlert, highlight: true },
];

export default function Services() {
  const { t } = useLanguage();
  const items = t("services.items");
  const sosPills = t("services.sosPills");
  const services = serviceMeta.map((m, i) => ({ ...m, ...items[i] }));

  return (
    <section
      id="services"
      data-testid={MASAR.sectionServices}
      className="relative py-24 sm:py-32 bg-neutral-900/40 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionLabel index="03" label={t("services.chapter")} />
        <div className="mt-4 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl">
            {t("services.titleA")}
            <br />
            {t("services.titleB")}
          </h2>
          <p className="text-neutral-400 max-w-md">
            {t("services.lede")}
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-6 gap-4 sm:gap-5">
          {services.map((s, i) => {
            const Icon = s.icon;
            const isFullFeature = s.highlight;

            if (isFullFeature) {
              return (
                <article
                  key={s.title}
                  className="group relative rounded-2xl p-6 sm:p-7 transition-all duration-300 sm:col-span-2 lg:col-span-6 bg-neutral-900/40 text-white masar-glow-ring overflow-hidden"
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#FF6A1A] text-white">
                      <Icon size={20} />
                    </span>
                    <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border border-white/30 text-white/80">
                      {s.tag}
                    </span>
                  </div>
                  <h3 className="mt-6 text-xl sm:text-2xl font-semibold tracking-tight">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-300 max-w-2xl">{s.desc}</p>
                  <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-4 gap-3">
                    {sosPills.map((p, j) => (
                      <SosPill key={p} n={String(j + 1).padStart(2, "0")} text={p} />
                    ))}
                  </div>
                </article>
              );
            }

            return (
              <TiltCard
                key={s.title}
                max={6}
                className={`group relative rounded-2xl p-6 sm:p-7 bg-neutral-950 border border-white/10 hover:border-[#FF6A1A] hover:shadow-[0_20px_50px_-25px_rgba(255,106,26,0.6)] ${
                  i < 2 ? "lg:col-span-3" : "lg:col-span-2"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-[#FF6A1A] group-hover:bg-[#FF6A1A] group-hover:text-white transition-colors">
                    <Icon size={20} />
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border border-white/15 text-neutral-400 group-hover:border-[#FF6A1A] group-hover:text-white transition-colors">
                    {s.tag}
                  </span>
                </div>
                <h3 className="mt-6 text-xl sm:text-2xl font-semibold tracking-tight">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400">{s.desc}</p>
              </TiltCard>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function SosPill({ n, text }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-neutral-950/5 px-4 py-3">
      <span className="font-mono text-xs text-[#FF6A1A]">{n}</span>
      <span className="text-sm text-neutral-100">{text}</span>
    </div>
  );
}
